import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import OTPVerification from '../components/OTPVerification';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const navigate = useNavigate();
  const { login, verifyLoginOTP } = useAuth();

  const getErrorMessage = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';
    console.log('Processing error message:', errorMessage);
    
    if (errorMessage.includes('invalid-credential') || errorMessage.includes('invalid email or password')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorMessage.includes('user-not-found') || errorMessage.includes('no account found')) {
      return 'No account found with this email address. Please check your email or sign up for a new account.';
    }
    if (errorMessage.includes('wrong-password')) {
      return 'Incorrect password. Please check your password and try again.';
    }
    if (errorMessage.includes('too-many-requests')) {
      return 'Too many failed login attempts. Please try again later or reset your password.';
    }
    if (errorMessage.includes('network-request-failed')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (errorMessage.includes('suspended')) {
      return 'Your account has been suspended. Please contact support for assistance.';
    }
    return 'Unable to sign in. Please try again or contact support if the problem persists.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('Starting login process...');
      const result = await login(email, password);
      console.log('Login result:', result);
      
      // If admin user, redirect to admin dashboard
      if (result.success && result.isAdmin) {
        console.log('Admin login successful, redirecting...');
        navigate('/admin');
        return;
      }

      // For regular users, show OTP verification
      console.log('Regular user login, showing OTP...');
      setTempUserData(result);
      setShowOTP(true);
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerify = async (otp) => {
    try {
      await verifyLoginOTP(tempUserData, otp);
      navigate('/dashboard');
    } catch (err) {
      throw new Error('Invalid OTP code. Please try again.');
    }
  };

  const handleOTPCancel = () => {
    setShowOTP(false);
    setTempUserData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      {showOTP ? (
        <OTPVerification
          type="login"
          onVerify={handleOTPVerify}
          onCancel={handleOTPCancel}
        />
      ) : (
        <div className="flex items-center justify-center px-4 py-20 min-h-[80vh]">
          <motion.div
            className="w-full max-w-md glass-card rounded-2xl shadow-2xl border border-slate-400/20 backdrop-blur-xl bg-slate-900/70 p-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col items-center mb-8">
              <Logo darkMode={true} />
              <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-white">
                Sign in to your account
              </h2>
            </div>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-lg bg-red-50/10 p-4 border border-red-400/30 mb-4"
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="mt-1 block w-full px-4 py-3 bg-slate-800/60 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-blue-200 mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    className="mt-1 block w-full px-4 py-3 bg-slate-800/60 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-blue-400 hover:text-blue-300">
                    Forgot your password?
                  </Link>
                </div>
              </div>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </motion.button>
              <div className="text-center">
                <p className="text-sm text-slate-400">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-medium text-blue-400 hover:text-blue-300">
                    Sign up
                  </Link>
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Login; 